import { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { MdQrCode2 } from 'react-icons/md';
import { TabsMobile, Flex } from '@kiper/ui';
import { useTranslation } from 'react-i18next';
import theme from '@kiper/theme';
import { useResizeQrCode } from '../hooks';
import { InfoGuest } from './components';
import FacialRegisterButton from '../../../../components/FacialRegisterButton';
import QrCode from '../../../../components/QrCode';
import FacialInvitation from '../../../../components/Modals/FacialInvitation/context/FacialInvitationProvider';
import {
  CalendarMonth,
  FamiliarFaceAndZoneIcon,
  HomePin,
} from '../../../../components/SvgIcons';
import inviteGuestExpired from '../../../../assets/images/invite-guest-expired.svg';
import facialInitialPageIcon from '../../../../assets/images/facial-initial-page-icon.svg';
import facialConfirmRegister from '../../../../assets/images/facial-confirm-register.svg';
import logoKiper from '../../../../assets/images/white-logo-kiper.svg';
import logoPorter from '../../../../assets/images/white-logo-porter.svg';
import { InviteGuestContext } from '../../../../store/InviteGuest';
import SmartLockCta from '../../components/SmartLockCta';
import { getInviteBrandVariant } from '../../constants';
import InviteError, {
  InviteErrorTypes,
} from '../../../../components/InviteError';
import * as S from './styles';

const TabsTypes = Object.freeze({
  FACIAL: 'Facial',
  QR_CODE: 'QR Code',
});

export default function MainInvite() {
  const [t] = useTranslation('invite');

  const { qrCodeSize } = useResizeQrCode();

  const {
    isIOS,
    hasQrCodeInvite,
    hasFacialInvite,
    inviteValid,
    inviteExpired,
    inviteInvalid,
    canOpenSmartLock,
    refetchInviteGuest,
    getPass,
    loadingPass,
    formattedValues,
    language,
    guestHash,
  } = useContext(InviteGuestContext);

  const [activeTab, setActiveTab] = useState(0);
  const [toggleModalFaceCapture, setToggleModalFaceCapture] = useState(false);

  const fallbackLanguage = useMemo(() => {
    const available = ['pt', 'en', 'es'];
    return available.includes(language) ? language : 'en';
  }, [language]);

  const tabs = [
    {
      title: TabsTypes.FACIAL,
      icon: (
        <FamiliarFaceAndZoneIcon
          color={activeTab === 0 ? 'white' : 'black'}
          size={20}
        />
      ),
    },
    {
      title: TabsTypes.QR_CODE,
      icon: <MdQrCode2 size={20} />,
    },
  ];

  const facialTabSelected = useMemo(
    () => tabs[activeTab].title === TabsTypes.FACIAL,
    [activeTab],
  );

  const handleChangeTab = useCallback(
    tab => {
      const activeTabIndex = tabs.findIndex(
        tabItem => tabItem.title === tab.title,
      );
      setActiveTab(activeTabIndex);
    },
    [activeTab],
  );

  useEffect(() => {
    if (hasFacialInvite) {
      setActiveTab(tabs.findIndex(tab => tab.title === TabsTypes.FACIAL));
    } else if (hasQrCodeInvite) {
      setActiveTab(tabs.findIndex(tab => tab.title === TabsTypes.QR_CODE));
    }
  }, [hasFacialInvite, hasQrCodeInvite]);

  const handleToggleModalFaceCapture = () =>
    setToggleModalFaceCapture(prevState => !prevState);

  const inviteRegisterStatusMessage = formattedValues.hasFaceId
    ? [
        {
          title: t('facial.confirm-register-title'),
          description: t('facial.confirm-register-message'),
          status: 'success',
          disabled: true,
          show: true,
        },
        {
          title: t('facial.update-button-title'),
          description: t('facial.update-button-message'),
          status: 'edit',
          disabled: false,
          show: formattedValues.allowFaceEdit,
        },
      ]
    : [
        {
          title: t('facial.register-button-title'),
          description: t('facial.register-button-message'),
          status: 'register',
          disabled: false,
          show: true,
        },
      ];

  return (
    <>
      {toggleModalFaceCapture && (
        <FacialInvitation
          onToggle={() => setToggleModalFaceCapture(prevState => !prevState)}
          guestHash={guestHash}
          refetch={refetchInviteGuest}
        />
      )}

      {formattedValues && (
        <S.Wrapper isPorter={formattedValues.isPorterPartner}>
          <S.Content $widthCardMax={qrCodeSize}>
            <S.InviteHeader>
              <S.Img
                src={formattedValues.isPorterPartner ? logoPorter : logoKiper}
                alt="kiper"
              />
            </S.InviteHeader>
            <Flex
              justifyContent="center"
              alignItems="center"
              gap="16px"
              width="100%"
            >
              {!(inviteExpired || inviteInvalid) &&
                hasFacialInvite &&
                hasQrCodeInvite && (
                  <TabsMobile
                    tabs={tabs}
                    active={activeTab}
                    onChangeTab={handleChangeTab}
                  />
                )}
            </Flex>
            <S.InviteContainer>
              <S.Invite>
                {inviteExpired && (
                  <Flex
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gridGap="16px"
                  >
                    <S.InviteInvalid background={inviteGuestExpired} />

                    <>
                      <S.Span warning>{t(`feedback.expired`)}</S.Span>
                      <S.Span>{t(`feedback.expired-context`)}</S.Span>
                    </>
                  </Flex>
                )}

                {inviteInvalid && (
                  <InviteError type={InviteErrorTypes.INVALID} />
                )}

                {!inviteInvalid && !inviteExpired && (
                  <>
                    {facialTabSelected ? (
                      <S.FacialRegisterIconContainer>
                        <S.FacialRegisterIcon
                          icon={
                            formattedValues.hasFaceId
                              ? facialConfirmRegister
                              : facialInitialPageIcon
                          }
                        />
                      </S.FacialRegisterIconContainer>
                    ) : (
                      <S.QrCodeContainer>
                        <S.QrCodeArea>
                          {inviteValid && (
                            <QrCode
                              value={formattedValues.hash}
                              size={qrCodeSize}
                              marginSize={14}
                            />
                          )}
                        </S.QrCodeArea>
                      </S.QrCodeContainer>
                    )}

                    <S.InviteInfo>
                      {canOpenSmartLock && (
                        <SmartLockCta
                          guestHash={guestHash}
                          variant={getInviteBrandVariant(
                            formattedValues.isPorterPartner,
                          )}
                        />
                      )}
                      {hasFacialInvite && (
                        <Flex
                          flexDirection="column"
                          alignItems="stretch"
                          gridGap="4px"
                        >
                          {inviteRegisterStatusMessage.map(item => (
                            <FacialRegisterButton
                              {...item}
                              key={`${item.title}-${item.status}`}
                              onClick={() => handleToggleModalFaceCapture()}
                            />
                          ))}
                        </Flex>
                      )}
                      <div>
                        <S.InviteTitle expired={+inviteExpired}>
                          {formattedValues.title}
                        </S.InviteTitle>
                        <InfoGuest
                          icon={
                            <CalendarMonth
                              color={theme.colors.porterPrimary}
                              size={32}
                            />
                          }
                          label={t('info.event.start')}
                          value={formattedValues.startInvite}
                          expired={inviteExpired}
                          isPorter={formattedValues.isPorterPartner}
                        />
                      </div>
                      {formattedValues.address && (
                        <InfoGuest
                          icon={
                            <HomePin
                              color={theme.colors.porterPrimary}
                              size={32}
                            />
                          }
                          label={t('info.event.place')}
                          value={formattedValues?.condominiumName}
                          expired={inviteExpired}
                          isPorter={formattedValues.isPorterPartner}
                          linkAddress={`${formattedValues?.addressLink}`}
                          address={`${formattedValues?.address}`}
                        />
                      )}
                    </S.InviteInfo>
                  </>
                )}
              </S.Invite>
            </S.InviteContainer>
            {!isIOS && !(inviteExpired || inviteInvalid) && (
              <S.GoogleWalletButton
                className={loadingPass && 'disabled'}
                onClick={() => getPass()}
                lang={fallbackLanguage}
                isPorter={formattedValues.isPorterPartner}
              />
            )}
          </S.Content>
        </S.Wrapper>
      )}
    </>
  );
}
